import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { styles } from '@actual-app/components/styles';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { BrujulaAnimada } from '#components/manager/subscribe/BrujulaAnimada';
import {
  ATARDECER,
  CIELO,
} from '#components/manager/subscribe/PuertoAtardecer';

/** Lo que tarda una carga antes de enseñarse (las rápidas no parpadean). */
export const RETRASO_CARGA_MS = 400;
/** Cuánto avanza el velero con cada paso nuevo de la carga. */
const AVANCE = 0.28;
const ANCHO = 210;
/** Cada cuánto cambia la frase de Gavi. */
export const CAMBIO_FRASE_MS = 2200;

/**
 * Pantalla de carga de Rumbo (30-oct-2026): el icono de la app sobre el
 * cielo del puerto y un velero que cruza las olas dejando una estela verde.
 * Cada frase nueva (`texto`) es un paso más, así que el velero avanza; nunca
 * llega del todo hasta que la carga termina y la pantalla se va. Debajo,
 * Gavi va diciendo frases al azar (Vadik, 30-oct-2026: «frases divertidas al
 * estilo Gavi»); el paso real de la carga solo lo oye el lector de pantalla.
 */
export function CargaRumbo({ texto }: { texto: string | null }) {
  const frase = useFraseGavi();
  const [visible, setVisible] = useState(false);
  const [pasos, setPasos] = useState(0);
  const ultimo = useRef(texto);

  useEffect(() => {
    const id = window.setTimeout(() => setVisible(true), RETRASO_CARGA_MS);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (texto !== ultimo.current) {
      ultimo.current = texto;
      setPasos(p => p + 1);
    }
  }, [texto]);

  if (!visible) {
    return null;
  }

  // 1 − (1 − AVANCE)^pasos: se acerca al final sin llegar nunca.
  const hecho = 0.12 + 0.78 * (1 - (1 - AVANCE) ** pasos);
  const largo = ANCHO + 20;

  return (
    <View
      data-testid="carga-rumbo"
      role="status"
      aria-live="polite"
      className={estilos.fundido}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 5000,
        background: CIELO,
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: ATARDECER.tinta,
      }}
    >
      <View
        aria-hidden
        style={{
          width: 96,
          height: 96,
          borderRadius: 26,
          background: ATARDECER.icono,
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 18px 30px -14px rgba(15,59,58,.6)',
        }}
      >
        <BrujulaAnimada size={78} escala={0.5} />
      </View>
      <svg
        viewBox={`0 0 ${ANCHO} 34`}
        width={ANCHO}
        height={34}
        aria-hidden
        style={{ marginTop: 40, overflow: 'visible' }}
      >
        <path
          d={OLAS}
          fill="none"
          stroke="#d9c39a"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d={OLAS}
          fill="none"
          stroke={ATARDECER.verde}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeDasharray={largo}
          strokeDashoffset={largo * (1 - hecho)}
          className={estilos.avance}
        />
        <g
          className={estilos.avance}
          style={{ transform: `translateX(${ANCHO * hecho}px)` }}
        >
          <g className={estilos.cabeceo}>
            <VeleroPequeno />
          </g>
        </g>
      </svg>
      <span
        key={frase}
        aria-hidden
        data-testid="carga-rumbo-frase"
        className={estilos.frase}
        style={{
          marginTop: 12,
          minHeight: 20,
          maxWidth: 300,
          padding: '0 16px',
          fontSize: 14,
          fontWeight: 600,
          color: ATARDECER.tinta2,
        }}
      >
        {frase}
      </span>
      {texto && <span style={styles.visuallyHidden}>{texto}</span>}
    </View>
  );
}

/**
 * Una frase de Gavi que cambia cada poco, en orden barajado: no se repite
 * ninguna hasta que han salido las diez.
 */
function useFraseGavi() {
  const { t } = useTranslation();
  const frases = [
    t('Stealing coins…'),
    t('Counting the coins twice, just in case…'),
    t('Looking for cents between the sofa cushions…'),
    t('Hiding chips from the other gulls…'),
    t('Haggling with the wind…'),
    t('Checking whether someone left a sandwich at the harbour…'),
    t('Tying up loose ends, and the odd account…'),
    t("Polishing the lighthouse, light isn't free…"),
    t('Fishing for a distracted fish…'),
    t('Asking the sea not to raise prices…'),
  ];
  const [orden] = useState(() => barajar(frases.length));
  const [vuelta, setVuelta] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setVuelta(v => v + 1), CAMBIO_FRASE_MS);
    return () => window.clearInterval(id);
  }, []);

  return frases[orden[vuelta % orden.length]];
}

/** 0…n−1 en orden aleatorio (Fisher-Yates). */
function barajar(n: number) {
  const orden = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [orden[i], orden[j]] = [orden[j], orden[i]];
  }
  return orden;
}

const OLAS = `M0 26 ${'q13 -5 26 0 t26 0 '.repeat(4)}`;

function VeleroPequeno() {
  return (
    <g transform="translate(-11 -2) scale(.22)">
      <path d="M0 102 L100 102 L84 120 L16 120 Z" fill="#f3ede0" />
      <path d="M16 120 L84 120 L80 124 L20 124 Z" fill={ATARDECER.rojo} />
      <line x1="48" y1="102" x2="48" y2="0" stroke="#2b3a42" strokeWidth="5" />
      <path d="M51 4 L51 96 L96 96 Z" fill="#fbf6ec" stroke="#e3d6bd" />
      <path d="M45 14 L45 96 L10 96 Z" fill="#e8cf96" />
      <path d="M48 0 L66 5 L48 10 Z" fill={ATARDECER.verde} />
    </g>
  );
}

const aparece = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });
const cabeceo = keyframes({
  '0%, 100%': { transform: 'rotate(-3deg)' },
  '50%': { transform: 'translateY(2px) rotate(3deg)' },
});

const estilos = {
  frase: css({
    animation: `${aparece} .35s ease-out both`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  }),
  fundido: css({
    animation: `${aparece} .3s ease-out both`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  }),
  avance: css({
    transition:
      'transform .9s cubic-bezier(.45,.05,.3,1), stroke-dashoffset .9s cubic-bezier(.45,.05,.3,1)',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  }),
  cabeceo: css({
    transformBox: 'fill-box',
    transformOrigin: '50% 90%',
    animation: `${cabeceo} 2.4s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  }),
};
